import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import cn from 'clsx'
import { useTranslation } from 'react-i18next'

import { PageTemplate } from '../../PageTemplate'
import { useShortcutApps } from './Shortcuts.hooks'
import styles from './Shortcuts.module.scss'
import { ShortcutsProps } from './Shortcuts.types'

const SLIDE_DELAY = 3000

export const Shortcuts: FC<ShortcutsProps> = ({
  setActivePage,
  completeTour
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'product_tour.shortcuts'
  })

  const shortcutApps = useShortcutApps()

  type ShortcutAppIds = (typeof shortcutApps)[number]
  const [activeApp, setActiveApp] = useState<ShortcutAppIds>(shortcutApps[0])

  const slideTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const startSlide = useCallback(() => {
    clearTimeout(slideTimeoutRef.current)
    slideTimeoutRef.current = setTimeout(() => {
      const activeAppIndex = shortcutApps.findIndex(
        app => app.id === activeApp.id
      )
      setActiveApp(shortcutApps[activeAppIndex + 1] || shortcutApps[0])
    }, SLIDE_DELAY)
  }, [activeApp.id, shortcutApps])

  useEffect(() => {
    startSlide()

    return () => {
      clearTimeout(slideTimeoutRef.current)
    }
  }, [startSlide])

  const handlers: {
    onMouseEnter: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
    onMouseLeave: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  } = {
    onMouseEnter: _ => {
      clearTimeout(slideTimeoutRef.current)
    },
    onMouseLeave: _ => {
      startSlide()
    }
  }

  return (
    <PageTemplate
      className={styles.shortcuts}
      navigationProps={{
        currentPage: 3,
        buttonLabel: t('navigate'),
        setActivePage,
        onNavigate: completeTour,
        animationDelay: 0
      }}
    >
      <div className={styles.texts}>
        <h1>{t('title')}</h1>
        <span>{t('description')}</span>
      </div>
      <div
        className={cn(styles.content, {
          [styles.firstApp]: activeApp.id === shortcutApps[0].id,
          [styles.lastApp]:
            activeApp.id === shortcutApps[shortcutApps.length - 1].id
        })}
      >
        <div className={styles.apps}>
          {shortcutApps.map(app => (
            <button
              key={app.id}
              className={cn(styles.app, {
                [styles.active]: activeApp.id === app.id
              })}
              onClick={() => setActiveApp(app)}
              {...(activeApp.id === app.id ? handlers : {})}
            >
              <img src={app.icon} width={48} height={48} />
              <span className={styles.name}>{app.name}</span>
            </button>
          ))}
        </div>
        <div className={styles.currentApp} {...handlers}>
          <img src={activeApp.imageUrl} alt={`${activeApp.name} app`} />
          <p>{activeApp.description}</p>
        </div>
      </div>
    </PageTemplate>
  )
}
